<!--
 *@Author: lixiang
 *@Date: 2026-01-14 11:30:00
 *@Description: 全局搜索模态框
-->
<script setup lang="ts">
const { isSearchOpen, closeSearch } = useSearch()
const { history, addHistory, removeHistory, clearHistory } = useSearchHistory()
const config = useRuntimeConfig()
const router = useRouter()

const searchInput = ref<HTMLInputElement | null>(null)
const searchQuery = ref('')
const isLoading = ref(false)
const activeIndex = ref(-1) // 键盘导航索引

// 搜索结果类型
interface SearchResults {
  articles: Array<{
    ArticleId: number
    Title: string
    Slug: string
    Summary: string | null
    CoverImage: string | null
    TypeName: string
    TypeSlug: string
    PublishedAt: string | null
  }>
  tags: Array<{
    TagId: number
    Name: string
    Slug: string
    Color: string | null
    ArticleCount: number
  }>
  series: Array<{
    SeriesId: number
    Name: string
    Slug: string
    Description: string | null
    ArticleCount: number
  }>
  images: Array<{
    ImageId: number
    Key: string
    Title: string | null
    Description: string | null
    BlurHash: string | null
    CategoryId: number | null
    CategoryName: string | null
  }>
  totals: {
    articles: number
    tags: number
    series: number
    images: number
  }
}

const results = ref<SearchResults | null>(null)

/**
 * 【性能优化】搜索结果缓存
 * 使用 useState 做全局缓存，缓存 5 分钟避免频繁请求
 */
const CACHE_DURATION = 5 * 60 * 1000 // 5 分钟

interface CachedSearchData {
  data: SearchResults
  timestamp: number
}

const searchCache = useState<Record<string, CachedSearchData>>('search-modal-cache', () => ({}))

// 防抖搜索
let debounceTimer: ReturnType<typeof setTimeout> | null = null
const debouncedSearch = (query: string) => {
  if (debounceTimer) clearTimeout(debounceTimer)
  
  if (!query.trim()) {
    results.value = null
    isLoading.value = false
    return
  }

  const trimmedQuery = query.trim()
  
  // 检查缓存
  const cached = searchCache.value[trimmedQuery]
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    results.value = cached.data
    isLoading.value = false
    return
  }

  isLoading.value = true
  debounceTimer = setTimeout(async () => {
    try {
      const data = await $fetch<SearchResults>(`${config.public.apiBase}/search`, {
        query: { q: trimmedQuery }
      })
      results.value = data
      
      // 存入缓存
      searchCache.value[trimmedQuery] = {
        data,
        timestamp: Date.now()
      }
      
      // 限制缓存数量，最多保存 30 个关键词
      const keys = Object.keys(searchCache.value)
      if (keys.length > 30 && keys[0]) {
        let oldestKey: string = keys[0]
        let oldestTime = searchCache.value[oldestKey]?.timestamp ?? Infinity
        for (const key of keys) {
          const time = searchCache.value[key]?.timestamp ?? Infinity
          if (time < oldestTime) {
            oldestTime = time
            oldestKey = key
          }
        }
        delete searchCache.value[oldestKey]
      }
    } catch (e) {
      console.error('Search failed:', e)
      results.value = null
    } finally {
      isLoading.value = false
    }
  }, 300)
}

// 监听输入变化
watch(searchQuery, (val) => {
  activeIndex.value = -1
  debouncedSearch(val)
})

// 计算所有可导航的结果项（图片作为整体入口，不单独列出）
const allResultItems = computed(() => {
  if (!results.value) return []
  const items: Array<{ type: 'article' | 'tag' | 'series' | 'image-link'; data: any }> = []
  
  results.value.articles.forEach(a => items.push({ type: 'article', data: a }))
  results.value.tags.forEach(t => items.push({ type: 'tag', data: t }))
  results.value.series.forEach(s => items.push({ type: 'series', data: s }))
  // 图片作为整体入口
  if (results.value.images.length > 0) {
    items.push({ type: 'image-link', data: { count: results.value.totals.images } })
  }
  
  return items
})

// 是否有搜索结果
const hasResults = computed(() => {
  if (!results.value) return false
  return results.value.articles.length > 0 || 
         results.value.tags.length > 0 || 
         results.value.series.length > 0 ||
         results.value.images.length > 0
})

// 是否显示空状态
const showEmptyState = computed(() => {
  return !isLoading.value && searchQuery.value.trim() && !hasResults.value
})

// 跳转到结果
const navigateTo = (path: string, keyword?: string) => {
  if (keyword) addHistory(keyword)
  closeSearch()
  router.push(path)
}

// 点击历史记录
const searchFromHistory = (keyword: string) => {
  searchQuery.value = keyword
  searchInput.value?.focus()
}

// 高亮关键词
const highlightKeyword = (text: string | null, keyword: string): string => {
  if (!text) return ''
  if (!keyword.trim()) return text
  
  const regex = new RegExp(`(${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
  return text.replace(regex, '<mark class="search-highlight">$1</mark>')
}

// 格式化日期
const formatDate = (dateStr: string | null) => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

// 监听打开状态
watch(isSearchOpen, (val) => {
  if (val) {
    nextTick(() => {
      searchInput.value?.focus()
    })
    if (import.meta.client) {
      document.body.style.overflow = 'hidden'
    }
  } else {
    // 关闭时重置状态
    searchQuery.value = ''
    results.value = null
    activeIndex.value = -1
    if (import.meta.client) {
      document.body.style.overflow = ''
    }
  }
})

// 键盘事件处理
const onKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && isSearchOpen.value) {
    closeSearch()
    return
  }
  
  // Ctrl + K 打开/关闭
  if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
    e.preventDefault()
    isSearchOpen.value = !isSearchOpen.value
    return
  }

  // 搜索框内的键盘导航
  if (!isSearchOpen.value) return

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    if (allResultItems.value.length > 0) {
      activeIndex.value = Math.min(activeIndex.value + 1, allResultItems.value.length - 1)
    }
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    activeIndex.value = Math.max(activeIndex.value - 1, -1)
  } else if (e.key === 'Enter' && activeIndex.value >= 0) {
    e.preventDefault()
    const item = allResultItems.value[activeIndex.value]
    if (item) {
      if (item.type === 'article') {
        navigateTo(`/blog/${item.data.Slug}`, searchQuery.value)
      } else if (item.type === 'tag') {
        navigateTo(`/blog/tag/${item.data.Slug}`, searchQuery.value)
      } else if (item.type === 'series') {
        navigateTo(`/series/${item.data.Slug}`, searchQuery.value)
      } else if (item.type === 'image-link') {
        // 跳转到图片搜索结果页
        navigateTo(`/search/image/${encodeURIComponent(searchQuery.value)}`, searchQuery.value)
      }
    }
  }
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown)
  if (debounceTimer) clearTimeout(debounceTimer)
})
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="isSearchOpen" class="search-overlay" @click.self="closeSearch">
        <div class="search-modal" role="dialog" aria-modal="true">
          
          <!-- 搜索头部 -->
          <div class="search-header">
            <svg width="20" height="20" viewBox="0 0 20 20" class="search-icon">
              <path d="M14.386 14.386l4.0877 4.0877-4.0877-4.0877c-2.9418 2.9419-7.7115 2.9419-10.6533 0-2.9419-2.9418-2.9419-7.7115 0-10.6533 2.9418-2.9419 7.7115-2.9419 10.6533 0 2.9419 2.9418 2.9419 7.7115 0 10.6533z" stroke="currentColor" fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
            <input 
              ref="searchInput" 
              v-model="searchQuery"
              type="text" 
              class="search-input" 
              placeholder="搜索文章、标签、系列..." 
              aria-label="搜索" 
            />
            <div v-if="isLoading" class="search-loading">
              <svg class="loading-spinner" width="18" height="18" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" fill="none" stroke-dasharray="31.4 31.4" stroke-linecap="round">
                  <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                </circle>
              </svg>
            </div>
            <button type="button" class="close-button" @click="closeSearch" aria-label="关闭搜索">
              <span class="kbd-esc">ESC</span>
              <span class="icon-close">×</span>
            </button>
          </div>

          <!-- 搜索内容区 -->
          <div class="search-body">
            
            <!-- 搜索历史（无搜索词时显示） -->
            <div v-if="!searchQuery.trim() && history.length > 0" class="search-history">
              <div class="section-header">
                <span class="section-title">搜索历史</span>
                <button class="clear-btn" @click="clearHistory">清空</button>
              </div>
              <div class="history-list">
                <div 
                  v-for="item in history" 
                  :key="item.keyword" 
                  class="history-item"
                  @click="searchFromHistory(item.keyword)"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <span class="history-keyword">{{ item.keyword }}</span>
                  <button class="remove-btn" @click.stop="removeHistory(item.keyword)" aria-label="删除">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- 空状态提示 -->
            <div v-else-if="!searchQuery.trim() && history.length === 0" class="empty-state">
              <p>输入关键词开始搜索</p>
            </div>

            <!-- 无结果 -->
            <div v-else-if="showEmptyState" class="empty-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <p>没有找到 "{{ searchQuery }}" 相关结果</p>
              <p class="hint">试试其他关键词？</p>
            </div>

            <!-- 搜索结果 -->
            <div v-else-if="hasResults" class="search-results">
              <!-- 文章结果 -->
              <div v-if="results?.articles.length" class="result-section">
                <div class="section-header">
                  <span class="section-title">📄 文章</span>
                  <span class="section-count">{{ results.totals.articles }} 条结果</span>
                </div>
                <div 
                  v-for="(article, idx) in results.articles" 
                  :key="article.ArticleId"
                  class="result-item article-item"
                  :class="{ active: activeIndex === idx }"
                  @click="navigateTo(`/blog/${article.Slug}`, searchQuery)"
                  @mouseenter="activeIndex = idx"
                >
                  <div class="result-content">
                    <h4 class="result-title" v-html="highlightKeyword(article.Title, searchQuery)"></h4>
                    <p v-if="article.Summary" class="result-summary" v-html="highlightKeyword(article.Summary, searchQuery)"></p>
                    <div class="result-meta">
                      <span class="type-badge">{{ article.TypeName }}</span>
                      <span v-if="article.PublishedAt" class="date">{{ formatDate(article.PublishedAt) }}</span>
                    </div>
                  </div>
                  <svg class="arrow-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </div>
              </div>

              <!-- 标签结果 -->
              <div v-if="results?.tags.length" class="result-section">
                <div class="section-header">
                  <span class="section-title">🏷️ 标签</span>
                </div>
                <div 
                  v-for="(tag, idx) in results.tags" 
                  :key="tag.TagId"
                  class="result-item tag-item"
                  :class="{ active: activeIndex === (results?.articles.length || 0) + idx }"
                  @click="navigateTo(`/blog/tag/${tag.Slug}`, searchQuery)"
                  @mouseenter="activeIndex = (results?.articles.length || 0) + idx"
                >
                  <div class="tag-info">
                    <span class="tag-name" :style="tag.Color ? { color: tag.Color } : {}">#{{ tag.Name }}</span>
                    <span class="tag-count">{{ tag.ArticleCount }} 篇文章</span>
                  </div>
                  <svg class="arrow-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </div>
              </div>

              <!-- 系列结果 -->
              <div v-if="results?.series.length" class="result-section">
                <div class="section-header">
                  <span class="section-title">📚 系列</span>
                </div>
                <div 
                  v-for="(series, idx) in results.series" 
                  :key="series.SeriesId"
                  class="result-item series-item"
                  :class="{ active: activeIndex === (results?.articles.length || 0) + (results?.tags.length || 0) + idx }"
                  @click="navigateTo(`/series/${series.Slug}`, searchQuery)"
                  @mouseenter="activeIndex = (results?.articles.length || 0) + (results?.tags.length || 0) + idx"
                >
                  <div class="series-info">
                    <h4 class="series-name" v-html="highlightKeyword(series.Name, searchQuery)"></h4>
                    <p v-if="series.Description" class="series-desc" v-html="highlightKeyword(series.Description, searchQuery)"></p>
                    <span class="series-count">{{ series.ArticleCount }} 篇文章</span>
                  </div>
                  <svg class="arrow-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </div>
              </div>

              <!-- 图片结果入口 -->
              <div 
                v-if="results?.images.length" 
                class="result-item image-link-item"
                :class="{ active: activeIndex === (results?.articles.length || 0) + (results?.tags.length || 0) + (results?.series.length || 0) }"
                @click="navigateTo(`/search/image/${encodeURIComponent(searchQuery)}`, searchQuery)"
                @mouseenter="activeIndex = (results?.articles.length || 0) + (results?.tags.length || 0) + (results?.series.length || 0)"
              >
                <div class="image-link-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                </div>
                <div class="image-link-content">
                  <span class="image-link-title">🖼️ 图片搜索结果</span>
                  <span class="image-link-count">找到 {{ results.totals.images }} 张相关图片</span>
                </div>
                <svg class="arrow-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </div>
            </div>

            <!-- 底部提示 -->
            <div class="search-footer">
              <div class="keyboard-hints">
                <span class="hint-item"><kbd>↑</kbd><kbd>↓</kbd> 导航</span>
                <span class="hint-item"><kbd>↵</kbd> 选择</span>
                <span class="hint-item"><kbd>esc</kbd> 关闭</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* 遮罩层动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 遮罩层：全屏固定 */
.search-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background-color: color-mix(in srgb, var(--bg-color, #fff), transparent 20%);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 20px;
}

/* 模态框主体 */
.search-modal {
  background-color: var(--panel-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  width: 100%;
  max-width: 640px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-top: 60px;
  transform-origin: center top;
  animation: modal-pop 0.2s ease-out;
}

/* 模态框头部 */
.search-header {
  display: flex;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid var(--border-color);
  position: relative;
  gap: 12px;
}

.search-icon {
  width: 20px;
  height: 20px;
  color: var(--muted-color);
  stroke-width: 2px;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 1.1rem;
  color: var(--text-color);
  padding: 0;
  outline: none;
  height: 36px;
}

.search-input::placeholder {
  color: var(--muted-color);
  opacity: 0.7;
}

.search-loading {
  color: var(--muted-color);
  display: flex;
  align-items: center;
}

.loading-spinner {
  opacity: 0.6;
}

.close-button {
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--muted-color);
  padding: 4px 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.close-button:hover {
  background: rgba(125, 125, 125, 0.1);
  color: var(--text-color);
}

.kbd-esc {
  display: none;
  font-size: 0.75rem;
  border: 1px solid var(--border-color);
  padding: 2px 8px;
  border-radius: 4px;
  color: var(--muted-color);
  font-family: inherit;
}

.icon-close {
  font-size: 24px;
  line-height: 1;
}

/* 模态框内容区 */
.search-body {
  min-height: 120px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* Element Plus 风格滚动条 */
.search-body::-webkit-scrollbar {
  width: 6px;
}

.search-body::-webkit-scrollbar-thumb {
  background-color: rgba(144, 147, 153, 0.3);
  border-radius: 10px;
}

.search-body::-webkit-scrollbar-thumb:hover {
  background-color: rgba(144, 147, 153, 0.5);
}

.search-body::-webkit-scrollbar-track {
  background-color: transparent;
}

/* 搜索历史 */
.search-history {
  padding: 16px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.section-title {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--muted-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.section-count {
  font-size: 0.75rem;
  color: var(--muted-color);
}

.clear-btn {
  background: none;
  border: none;
  color: var(--muted-color);
  font-size: 0.8rem;
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 4px;
  transition: all 0.15s;
}

.clear-btn:hover {
  color: var(--text-color);
  background: rgba(125, 125, 125, 0.1);
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.history-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  color: var(--muted-color);
  transition: all 0.15s;
}

.history-item:hover {
  background: rgba(125, 125, 125, 0.08);
  color: var(--text-color);
}

.history-keyword {
  flex: 1;
  font-size: 0.95rem;
  color: var(--text-color);
}

.remove-btn {
  background: none;
  border: none;
  color: var(--muted-color);
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0;
  transition: all 0.15s;
}

.history-item:hover .remove-btn {
  opacity: 1;
}

.remove-btn:hover {
  background: rgba(125, 125, 125, 0.15);
  color: var(--text-color);
}

/* 空状态 */
.empty-state {
  text-align: center;
  color: var(--muted-color);
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.empty-state svg {
  opacity: 0.4;
}

.empty-state .hint {
  font-size: 0.85rem;
  opacity: 0.7;
}

/* 搜索结果 */
.search-results {
  padding: 8px;
}

.result-section {
  margin-bottom: 8px;
}

.result-section .section-header {
  padding: 8px 12px;
  margin-bottom: 4px;
}

.result-item {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
  gap: 12px;
}

.result-item:hover,
.result-item.active {
  background: rgba(125, 125, 125, 0.08);
}

.result-item.active {
  background: rgba(125, 125, 125, 0.12);
}

.result-content,
.tag-info,
.series-info {
  flex: 1;
  min-width: 0;
}

.result-title,
.series-name {
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--text-color);
  margin: 0 0 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-summary,
.series-desc {
  font-size: 0.85rem;
  color: var(--muted-color);
  margin: 0 0 6px 0;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.result-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.8rem;
}

.type-badge {
  padding: 2px 8px;
  background: rgba(125, 125, 125, 0.1);
  border-radius: 4px;
  color: var(--muted-color);
}

.date {
  color: var(--muted-color);
}

.tag-name {
  font-size: 0.95rem;
  font-weight: 500;
}

.tag-count,
.series-count {
  font-size: 0.8rem;
  color: var(--muted-color);
}

.arrow-icon {
  color: var(--muted-color);
  opacity: 0;
  transition: all 0.15s;
  flex-shrink: 0;
}

.result-item:hover .arrow-icon,
.result-item.active .arrow-icon {
  opacity: 1;
}

/* 图片搜索入口 */
.image-link-item {
  margin: 8px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(147, 51, 234, 0.08) 100%);
  border: 1px solid rgba(59, 130, 246, 0.2);
}

.image-link-item:hover,
.image-link-item.active {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(147, 51, 234, 0.15) 100%);
  border-color: rgba(59, 130, 246, 0.3);
}

.image-link-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(59, 130, 246, 0.1);
  border-radius: 10px;
  color: var(--primary-color, #3b82f6);
}

.image-link-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.image-link-title {
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--text-color);
}

.image-link-count {
  font-size: 0.8rem;
  color: var(--muted-color);
}

/* 高亮关键词 */
:deep(.search-highlight) {
  background: rgba(255, 213, 0, 0.4);
  color: inherit;
  padding: 0 2px;
  border-radius: 2px;
}

:global(html[data-theme="dark"]) :deep(.search-highlight) {
  background: rgba(255, 213, 0, 0.25);
}

/* 底部提示 */
.search-footer {
  padding: 12px 16px;
  border-top: 1px solid var(--border-color);
  margin-top: auto;
}

.keyboard-hints {
  display: flex;
  justify-content: center;
  gap: 20px;
  font-size: 0.75rem;
  color: var(--muted-color);
}

.hint-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.hint-item kbd {
  display: inline-block;
  padding: 2px 6px;
  font-size: 0.7rem;
  font-family: inherit;
  background: rgba(125, 125, 125, 0.1);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  min-width: 20px;
  text-align: center;
}

@keyframes modal-pop {
  from {
    opacity: 0;
    transform: scale(0.96) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* 移动端适配 */
@media (max-width: 639px) {
  .search-overlay {
    padding: 0;
    background-color: var(--bg-color);
    align-items: stretch;
  }

  .search-modal {
    margin-top: 0;
    max-width: none;
    width: 100%;
    height: 100%;
    border-radius: 0;
    border: none;
    box-shadow: none;
  }
  
  .search-body {
    max-height: none;
    flex: 1;
  }

  .icon-close {
    display: block;
  }

  .kbd-esc {
    display: none;
  }

  .keyboard-hints {
    display: none;
  }
}

@media (min-width: 640px) {
  .icon-close {
    display: none;
  }

  .kbd-esc {
    display: inline-block;
  }
}

/* 暗黑模式 */
:global(html[data-theme="dark"]) .search-overlay {
  background-color: rgba(0, 0, 0, 0.7);
}
</style>
